/**
 * Prefetch the map code ON INTENT (slice 56f).
 *
 * The fleet map (MapCanvas + maplibre-gl, ~230 kB gz — the app's largest chunk) is
 * lazy-loaded, so the FIRST map open paid the whole download after the tap. We
 * start that download the moment the user reaches for a map — pointerdown / hover /
 * focus on a Live-map link — so it overlaps the tap instead of following it.
 *
 * 🔴 NOT on idle after sign-in. That was the first version, and
 * `ci/check-overview-render.mjs` M-4 failed it: `/` is the login critical path and
 * maplibre must not download for a user who never opens a map (a Mogadishu data
 * bill). The earlier slice's guarantee stands; intent is the industry-standard way
 * to get the speed without the cost. After the first download the service worker
 * serves the hashed chunk from its cache, so later opens are instant anyway.
 * Once per page; failure is harmless (the route's own lazy import retries).
 */
let started = false;

/** True when the user asked the browser to save data (Android "Data saver"). The
 *  prefetch is an optimisation, never worth a metered user's megabytes. */
function saveData(): boolean {
  const c = (globalThis.navigator as { connection?: { saveData?: boolean } } | undefined)?.connection;
  return c?.saveData === true;
}

/**
 * Warm the connection to Google's tile host (slice 56f). Called from the SIGNED-IN
 * shell only — never from index.html, which also serves the public share page
 * (Street-only, no Google path; CLAUDE.md). Only when a Google key is baked in.
 */
export function preconnectGoogleTiles(doc: Document = document, hasKey: boolean): void {
  if (!hasKey || doc.querySelector('link[rel="preconnect"][href="https://tile.googleapis.com"]')) return;
  const l = doc.createElement('link');
  l.rel = 'preconnect';
  l.href = 'https://tile.googleapis.com';
  l.crossOrigin = 'anonymous';
  doc.head.appendChild(l);
}

export function prefetchMapChunk(
  load: () => Promise<unknown> = () => import('@/features/live/MapCanvas'),
  idle: (cb: () => void) => void = (cb) => cb(),
): void {
  if (started || saveData()) return;
  started = true;
  idle(() => {
    void load().catch(() => {
      started = false; // allow a later attempt
    });
  });
}

/** Spread onto a link that leads to a map: the chunk starts downloading as the
 *  finger lands (pointerdown precedes click by ~100 ms on touch), on hover (desktop)
 *  and on keyboard focus. */
export const mapIntentHandlers = {
  onPointerDown: () => prefetchMapChunk(),
  onMouseEnter: () => prefetchMapChunk(),
  onFocus: () => prefetchMapChunk(),
};

/** Test-only: reset the once-per-page latch. */
export function __resetPrefetchForTests(): void {
  started = false;
}
